import React, { useCallback, useEffect, useState } from 'react';
import {
  Form, Input, Select, DatePicker, Switch, Button, Card, Typography, message, Radio, Row, Col, Spin,
  Collapse, Table,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api/client';
import { getEmployeeAuditLog, AuditEntry } from '../api/audit';
import { getWorkplaces, Workplace } from '../api/workplaces';
import { useCascadeSelect } from '../hooks/useCascadeSelect';

const { Title } = Typography;
const { Option } = Select;

interface Department {
  id: number;
  name: string;
}

interface Position {
  id: number;
  name: string;
}

const UNIFORM_HEIGHTS = ['158-164', '164-170', '170-176', '176-182', '182-188', '188-194'];
const UNIFORM_SIZES = ['88-92', '92-96', '96-100', '100-104', '104-108', '108-112', '112-116', '116-120'];

const EmployeeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  // Полные списки — только для резолвера аудит-лога (чтобы по ID найти имя)
  const [allPositions, setAllPositions] = useState<Position[]>([]);
  const [allWorkplaces, setAllWorkplaces] = useState<Workplace[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);

  // Стабильные функции загрузки для каскадных хуков
  const fetchPositionsByDept = useCallback(
    (deptId: number) =>
      api.get('/positions', { params: { department_id: deptId } }).then(r => r.data),
    [],
  );
  const fetchWorkplacesByDept = useCallback(
    (deptId: number) =>
      api.get('/workplaces', { params: { department_id: deptId } })
        .then(r => r.data.map((w: any) => ({ id: w.id, name: w.name }))),
    [],
  );

  // Каскадные хуки: должность и рабочее место зависят от подразделения
  const positionSelect = useCascadeSelect({
    form,
    watchField: 'department_id',
    clearField: 'position_id',
    fetchOptions: fetchPositionsByDept,
  });
  const workplaceSelect = useCascadeSelect({
    form,
    watchField: 'department_id',
    clearField: 'workplace_id',
    fetchOptions: fetchWorkplacesByDept,
  });

  const loadAuditLog = async (page = 1) => {
    if (!id) return;
    setAuditLoading(true);
    try {
      const data = await getEmployeeAuditLog(Number(id), page);
      setAuditLog(data.items ?? (data as any));
      setAuditTotal(data.total ?? 0);
      setAuditPage(page);
    } finally {
      setAuditLoading(false);
    }
  };

  const FIELD_LABELS: Record<string, string> = {
    tab_number: 'Таб. номер',
    last_name: 'Фамилия',
    first_name: 'Имя',
    middle_name: 'Отчество',
    phone: 'Телефон',
    birth_date: 'Дата рождения',
    hire_date: 'Дата приёма',
    status: 'Статус',
    status_since: 'Статус с',
    organization: 'Организация',
    gender: 'Пол',
    is_official: 'Официально',
    department_id: 'Подразделение',
    position_id: 'Должность',
    workplace_id: 'Рабочее место',
    uniform_height: 'Рост (форма)',
    uniform_size: 'Размер (форма)',
    marital_status: 'Семейное положение',
    has_children: 'Дети',
    snils: 'СНИЛС',
  };

  const ACTION_LABELS: Record<string, string> = {
    create: 'Создание',
    update: 'Изменение',
    delete: 'Увольнение',
  };

  const resolveValue = (field: string, value: any): string => {
    if (value === null || value === undefined || value === 'None' || value === 'null') return '—';
    const str = String(value);

    if (field === 'status') {
      const m: Record<string, string> = {
        active: 'Работает', on_leave: 'В отпуске', sick: 'На больничном', dismissed: 'Уволен',
      };
      return m[str] ?? str;
    }
    if (field === 'is_official' || field === 'has_children') {
      return (str === 'true' || str === 'True') ? 'Да' : 'Нет';
    }
    if (field === 'marital_status') {
      return str === 'married' ? 'Женат/Замужем' : 'Не состоит в браке';
    }
    if (field === 'birth_date' || field === 'hire_date' || field === 'status_since') {
      const d = dayjs(str);
      return d.isValid() ? d.format('DD.MM.YYYY') : str;
    }
    if (field === 'department_id') {
      const found = departments.find(d => d.id === Number(value));
      return found ? found.name : `подразделение #${str}`;
    }
    if (field === 'position_id') {
      const found = allPositions.find(p => p.id === Number(value));
      return found ? found.name : `должность #${str}`;
    }
    if (field === 'workplace_id') {
      const found = allWorkplaces.find(w => w.id === Number(value));
      return found ? found.name : `рабочее место #${str}`;
    }
    return str;
  };

  const renderChanges = (changes: Record<string, any>, action: string) => {
    if (!changes || Object.keys(changes).length === 0) return <span style={{ color: '#999' }}>—</span>;

    // Запись о создании: { employee: { все поля } }
    if (action === 'create') {
      return <span style={{ color: '#52c41a' }}>Сотрудник добавлен в систему</span>;
    }
    // Запись об увольнении: { status: { old, new } }
    if (action === 'delete') {
      return <span style={{ color: '#ff4d4f' }}>Сотрудник уволен</span>;
    }

    // Обычные изменения
    const entries = Object.entries(changes).filter(([field]) => field in FIELD_LABELS);
    if (entries.length === 0) return <span style={{ color: '#999' }}>—</span>;

    return (
      <div style={{ fontSize: 12, lineHeight: '20px' }}>
        {entries.map(([field, diff]) => {
          const label = FIELD_LABELS[field] ?? field;
          const oldVal = resolveValue(field, diff?.old);
          const newVal = resolveValue(field, diff?.new);
          return (
            <div key={field}>
              <span style={{ color: '#666' }}>{label}:</span>{' '}
              {oldVal !== '—' && (
                <span style={{ color: '#aaa', textDecoration: 'line-through' }}>{oldVal}</span>
              )}
              {oldVal !== '—' && ' → '}
              <span style={{ color: '#52c41a', fontWeight: 500 }}>{newVal}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const auditColumns = [
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'date',
      width: 140,
      render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Кто',
      key: 'user',
      width: 130,
      render: (_: any, record: AuditEntry) =>
        record.display_name || record.username || `пользователь #${record.user_id}`,
    },
    {
      title: 'Действие',
      dataIndex: 'action',
      key: 'action',
      width: 110,
      render: (action: string) => ACTION_LABELS[action] ?? action,
    },
    {
      title: 'Что изменилось',
      key: 'changes',
      render: (_: any, record: AuditEntry) => renderChanges(record.changes, record.action),
    },
  ];

  useEffect(() => {
    api.get('/departments').then((res) => setDepartments(res.data));
    // Все позиции и рабочие места нужны только для резолвера аудит-лога
    api.get('/positions').then((res) => setAllPositions(res.data));
    getWorkplaces().then(setAllWorkplaces);

    // Auto-generate tab number for new employees
    if (!isEdit) {
      api.get('/employees/next-tab-number').then((res) => {
        form.setFieldValue('tab_number', res.data.tab_number);
      });
    }
  }, [isEdit, form]);

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      api.get(`/employees/${id}`)
        .then(async (res) => {
          const data = res.data;
          const deptId: number | undefined = data.department?.id;

          // Сначала инициализируем каскадные хуки: загружают опции БЕЗ очистки полей
          if (deptId) {
            await Promise.all([
              positionSelect.init(deptId),
              workplaceSelect.init(deptId),
            ]);
          }

          // Затем заполняем форму (хуки уже видели deptId → не будут сбрасывать поля)
          form.setFieldsValue({
            ...data,
            birth_date: data.birth_date ? dayjs(data.birth_date) : null,
            hire_date: data.hire_date ? dayjs(data.hire_date) : null,
            position_id: data.position?.id,
            department_id: deptId,
            workplace_id: data.workplace_id,
            marital_status: data.marital_status === 'married',
          });
        })
        .catch(() => message.error('Ошибка загрузки данных сотрудника'))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, form]); // positionSelect.init / workplaceSelect.init намеренно не в deps (стабильны)

  const onFinish = async (values: any) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null,
        hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : null,
        marital_status: values.marital_status ? 'married' : 'single',
      };

      if (isEdit) {
        await api.put(`/employees/${id}`, payload);
        message.success('Сотрудник обновлён');
      } else {
        await api.post('/employees', payload);
        message.success('Сотрудник добавлен');
      }
      navigate('/employees');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((d: any) => message.error(d.msg));
      } else {
        message.error(detail || 'Ошибка сохранения');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/employees')}
        style={{ marginBottom: 16 }}
      >
        Назад к списку
      </Button>

      <Title level={4}>{isEdit ? 'Редактирование сотрудника' : 'Новый сотрудник'}</Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          organization: 'ООО АвтоРегиум',
          is_official: true,
          has_children: false,
          marital_status: false,
          gender: 'М',
        }}
        style={{ maxWidth: 900 }}
      >
        <Card title="Основное" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="tab_number" label="Табельный номер" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <Input placeholder="0042" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="organization" label="Организация" rules={[{ required: true }]}>
                <Select>
                  <Option value="ООО АвтоРегиум">ООО АвтоРегиум</Option>
                  <Option value="ИП Фомин Д.А.">ИП Фомин Д.А.</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Личные данные" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="last_name" label="Фамилия" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="first_name" label="Имя" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="middle_name" label="Отчество" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="gender" label="Пол" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <Radio.Group>
                  <Radio value="М">М</Radio>
                  <Radio value="Ж">Ж</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="birth_date" label="Дата рождения" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="phone"
                label="Телефон"
                rules={[
                  { required: true, message: 'Обязательное поле' },
                  { pattern: /^7\d{10}$/, message: '11 цифр, начинается с 7' },
                ]}
              >
                <Input placeholder="79998887766" maxLength={11} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="snils"
                label="СНИЛС"
                rules={[
                  { required: true, message: 'Обязательное поле' },
                  { pattern: /^\d{3}-\d{3}-\d{3}\s\d{2}$/, message: 'Формат: XXX-XXX-XXX XX' },
                ]}
              >
                <Input placeholder="123-456-789 01" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Работа" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="department_id"
                label="Подразделение"
                rules={[{ required: true, message: 'Выберите подразделение' }]}
              >
                <Select placeholder="Выберите" allowClear>
                  {departments.map((d) => (
                    <Option key={d.id} value={d.id}>{d.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="position_id"
                label="Должность"
                rules={[{ required: true, message: 'Выберите должность' }]}
              >
                <Select
                  placeholder={positionSelect.disabled ? 'Сначала выберите подразделение' : 'Выберите'}
                  allowClear
                  disabled={positionSelect.disabled}
                  loading={positionSelect.loading}
                  options={positionSelect.options}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="workplace_id"
                label="Рабочее место"
                rules={[{ required: true, message: 'Выберите рабочее место' }]}
              >
                <Select
                  placeholder={workplaceSelect.disabled ? 'Сначала выберите подразделение' : 'Выберите'}
                  allowClear
                  disabled={workplaceSelect.disabled}
                  loading={workplaceSelect.loading}
                  options={workplaceSelect.options}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="hire_date" label="Дата приёма" rules={[{ required: true, message: 'Обязательное поле' }]}>
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="is_official" label="Официально" valuePropName="checked">
                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Размер одежды" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="uniform_height" label="Рост">
                <Select placeholder="Выберите" allowClear>
                  {UNIFORM_HEIGHTS.map((h) => (
                    <Option key={h} value={h}>{h}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="uniform_size" label="Размер">
                <Select placeholder="Выберите" allowClear>
                  {UNIFORM_SIZES.map((s) => (
                    <Option key={s} value={s}>{s}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Семейное положение" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="marital_status" label="Женат / Замужем" valuePropName="checked">
                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="has_children" label="Дети" valuePropName="checked">
                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {isEdit && (
          <Collapse
            style={{ marginBottom: 16 }}
            onChange={(keys) => {
              if (keys.includes('audit') && auditLog.length === 0) loadAuditLog(1);
            }}
            items={[{
              key: 'audit',
              label: 'Лог изменений',
              children: (
                <Table
                  size="small"
                  columns={auditColumns}
                  dataSource={auditLog}
                  rowKey="id"
                  loading={auditLoading}
                  pagination={{
                    current: auditPage,
                    pageSize: 20,
                    total: auditTotal,
                    onChange: loadAuditLog,
                    showSizeChanger: false,
                  }}
                />
              ),
            }]}
          />
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="large">
            {isEdit ? 'Сохранить изменения' : 'Добавить сотрудника'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EmployeeForm;
